import { formatDistanceToNow } from 'date-fns'
import {
  Archive,
  ArchiveRestore,
  Building2,
  Calendar,
  ChevronRight,
  CircleUser,
  Clock,
  CreditCard,
  ExternalLink,
  Mail,
  MapPin,
  MessageSquare,
  PawPrint,
  Pencil,
  Phone,
  Receipt,
  Stethoscope,
  VenetianMask,
} from 'lucide-react'
import { useState } from 'react'
import { generatePath, Link, useParams } from 'react-router-dom'

import type {
  AppointmentWithRelations,
  CommunicationWithPatient,
  GetMemberConversationsResponse,
  Invoice,
  Member,
  Patient,
  RegionId,
} from '@snootsvet/shared'
import {
  type DateFormat,
  formatCurrency,
  formatName,
  isEmptyHtml,
  resolveDayGroup,
  titleCase,
} from '@snootsvet/shared'

import { CopyButton } from '@/frontend/components/copy-button'
import {
  FormattedDate,
  FormattedDateTime,
  recordFrame,
  useViewerFormat,
} from '@/frontend/components/formatted-datetime'
import { InternalNotesDialog } from '@/frontend/components/internal-notes-dialog'
import { normaliseForDisplay } from '@/frontend/components/internal-notes-utils'
import { nowDividerIndex, TimelineNowDivider } from '@/frontend/components/timeline-now-divider'
import { Alert, AlertDescription, AlertTitle } from '@/frontend/components/ui/alert'
import { Badge } from '@/frontend/components/ui/badge'
import { Button } from '@/frontend/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/frontend/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/frontend/components/ui/dialog'
import { ExpandableContent } from '@/frontend/components/ui/expandable-content'
import { Input } from '@/frontend/components/ui/input'
import { RichTextContent } from '@/frontend/components/ui/rich-text-content'
import { Spinner } from '@/frontend/components/ui/spinner'
import { UpcomingBadge } from '@/frontend/components/upcoming-badge'
import { AppointmentStatusBadge } from '@/frontend/features/appointments/appointment-status-badge'
import { getDisplayStatus } from '@/frontend/features/appointments/appointments.utils'
import { SubscriptionBadges } from '@/frontend/features/billing/subscription-badges'
import { CommunicationEntry } from '@/frontend/features/communications/communication-entry'
import { ArchiveMemberDialog } from '@/frontend/features/members/archive-member-action'
import { MemberInvoicesCard } from '@/frontend/features/members/member-invoices-card'
import { UpdateMemberForm } from '@/frontend/features/members/update-member.form'
import { CreatePatientForm } from '@/frontend/features/patient/create-patient.form'
import { SpeciesIcon } from '@/frontend/features/patient/species-icon'
import { usePatientAttributes } from '@/frontend/features/patient/use-patient-attributes'
import { RegionFlag } from '@/frontend/features/region/region-flag'
import { useGetPatientSubscription } from '@/frontend/hooks/api/patients/use-get-patient-subscription'
import { useListPatientInvoices } from '@/frontend/hooks/api/patients/use-list-patient-invoices'
import { useGetAuthenticatedStaff } from '@/frontend/hooks/api/use-get-authenticated-staff'
import { useGetMember } from '@/frontend/hooks/api/use-get-member'
import { useImpersonateMember } from '@/frontend/hooks/api/use-impersonate-member'
import { useListMemberAppointments } from '@/frontend/hooks/api/use-list-member-appointments'
import { useListMemberCommunications } from '@/frontend/hooks/api/use-list-member-communications'
import { useListMemberConversations } from '@/frontend/hooks/api/use-list-member-conversations'
import { useUpdateMember } from '@/frontend/hooks/api/use-update-member'
import { useBreadcrumbs } from '@/frontend/hooks/use-breadcrumbs'
import { useDocumentTitle } from '@/frontend/hooks/use-document-title'
import { type StripeIdValidationResult, validateStripeId } from '@/frontend/lib/stripe'
import { cn } from '@/frontend/lib/utils'
import { routes } from '@/frontend/routes'

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/** Formats an ISO timestamp as a relative time like "3 minutes ago". */
function formatRelativeTime(iso: string): string {
  return formatDistanceToNow(new Date(iso), { addSuffix: true })
}

/** Formats a member's address as a single comma-separated line. */
function formatAddress(member: Member): string {
  const parts = [member.address1, member.address2]
  if (member.region === 'us') {
    // US: "City, ST ZIP"
    const cityState = [member.city, member.state].filter(Boolean).join(', ')
    parts.push([cityState, member.postalCode].filter(Boolean).join(' '))
  } else {
    // UK: "Postcode City, Country"
    parts.push([member.postalCode, member.city].filter(Boolean).join(' '))
    parts.push(member.country)
  }
  return parts.filter(Boolean).join(', ')
}

/** Maps Stripe customer ID validation results to member-facing copy. */
function getStripeCustomerIdError(result: StripeIdValidationResult): string | null {
  if (result === 'valid') return null
  if (result === 'wrong_entity') {
    return 'That appears to be a Stripe subscription ID. Members should be linked to a Stripe customer ID beginning with cus_.'
  }
  return 'Enter a Stripe customer ID beginning with cus_.'
}

export type TimelineItem =
  | { type: 'appointment'; date: string; data: AppointmentWithRelations }
  | {
      type: 'conversation'
      date: string
      data: GetMemberConversationsResponse['conversations'][number]
    }
  | { type: 'invoice'; date: string; data: Invoice & { petName: string } }
  | { type: 'communication'; date: string; data: CommunicationWithPatient }

/**
 * Groups timeline items by date label, preserving order.
 *
 * `resolveDayGroup` returns the label and whether the day is still ahead from one
 * comparison, so a group headed "Tomorrow" is always flagged upcoming.
 */
function groupByDate(
  items: TimelineItem[],
  viewerFmt: DateFormat
): { label: string; isFuture: boolean; items: TimelineItem[] }[] {
  const groups: { label: string; isFuture: boolean; items: TimelineItem[] }[] = []

  for (const item of items) {
    // Only appointments happened somewhere; the rest resolve in the viewer's frame.
    const frame = recordFrame(item.type === 'appointment' ? item.data.clinicId : null, viewerFmt)
    const { label, isFuture } = resolveDayGroup(item.date, frame)
    const last = groups[groups.length - 1]
    if (last?.label === label) {
      last.items.push(item)
    } else {
      groups.push({ label, isFuture, items: [item] })
    }
  }

  return groups
}

const INVOICE_STATUS_COLORS = {
  draft: 'neutral',
  paid: 'success',
  open: 'pending',
  uncollectible: 'destructive',
  void: 'neutral',
} as const

/** Maps a Stripe invoice status to its display label and colour token. */
function getInvoiceStatusDisplay(status: Invoice['status']) {
  return {
    statusLabel: titleCase(status),
    statusColor: INVOICE_STATUS_COLORS[status],
  }
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                    */
/* ------------------------------------------------------------------ */

/** Icon on the timeline connector, matching the patient timeline style. */
function TimelineIcon({ type, isFuture }: { type: TimelineItem['type']; isFuture: boolean }) {
  const base = cn(
    'flex size-6 shrink-0 items-center justify-center rounded-full border bg-card ring-3 ring-background',
    isFuture && 'border-dashed'
  )

  switch (type) {
    case 'appointment':
      return (
        <div className={base}>
          <Calendar className="size-3 text-muted-foreground" />
        </div>
      )
    case 'conversation':
      return (
        <div className={base}>
          <MessageSquare className="size-3 text-muted-foreground" />
        </div>
      )
    case 'invoice':
      return (
        <div className={base}>
          <Receipt className="size-3 text-muted-foreground" />
        </div>
      )
    case 'communication':
      return (
        <div className={base}>
          <Mail className="size-3.5 text-communication" />
        </div>
      )
  }
}

/** A single appointment entry in the activity timeline. */
function AppointmentItem({
  appointment,
  isFuture,
}: {
  appointment: AppointmentWithRelations
  isFuture: boolean
}) {
  const vetName = formatName(appointment.staff)

  return (
    <Link
      to={`${generatePath(routes.patient, { patientId: appointment.patient.id })}#${appointment.id}`}
      className="block"
    >
      <Card className={cn('py-3 shadow-none transition-colors hover:border-foreground/20')}>
        <CardContent className="space-y-1 p-0 px-4">
          <div className="flex items-center gap-2">
            <span className="text-headline-sm text-balance">
              {appointment.appointmentTypeName ?? 'Appointment'}
            </span>
            <AppointmentStatusBadge status={getDisplayStatus(appointment)} size="sm" />
            {isFuture && <UpcomingBadge size="default" />}
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <SpeciesIcon species={appointment.patient.species} className="size-3" />
              {appointment.patient.name}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock className="size-3 shrink-0" />
              <FormattedDateTime
                value={appointment.startTime}
                clinicId={appointment.clinic?.id ?? null}
              />
            </span>
            {appointment.clinic && (
              <span className="inline-flex items-center gap-1">
                <Building2 className="size-3 shrink-0" />
                {appointment.clinic.name}
              </span>
            )}
            {vetName && (
              <span className="inline-flex items-center gap-1">
                <Stethoscope className="size-3 shrink-0" />
                {vetName}
              </span>
            )}
          </div>

          {appointment.visitReason && (
            <p className="text-sm text-muted-foreground">{appointment.visitReason}</p>
          )}
        </CardContent>
      </Card>
    </Link>
  )
}

/** A single Intercom conversation entry in the activity timeline. */
function ConversationItem({
  conversation,
}: {
  conversation: GetMemberConversationsResponse['conversations'][number]
}) {
  return (
    <a href={conversation.intercomUrl} target="_blank" rel="noopener noreferrer" className="block">
      <Card className="py-3 shadow-none transition-colors hover:border-foreground/20">
        <CardContent className="space-y-1 p-0 px-4">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-balance">
              {conversation.title || 'Conversation'}
            </span>
            <ConversationStateBadge state={conversation.state} />
          </div>

          {conversation.sourceBody && (
            <p className="text-sm text-muted-foreground line-clamp-2 break-words">
              {conversation.sourceBody}
            </p>
          )}

          <div className="flex items-center gap-1 text-sm text-muted-foreground/60">
            <ExternalLink className="size-3" />
            <span>View in Intercom</span>
          </div>
        </CardContent>
      </Card>
    </a>
  )
}

/** A single invoice/payment entry in the activity timeline. */
function InvoiceItem({ invoice }: { invoice: Invoice & { petName: string } }) {
  const { statusLabel, statusColor } = getInvoiceStatusDisplay(invoice.status)

  const amount = formatCurrency(
    invoice.status === 'paid' ? invoice.amountPaid : invoice.amountDue,
    invoice.currency
  )

  const content = (
    <Card className="py-3 shadow-none transition-colors hover:border-foreground/20">
      <CardContent className="space-y-1 p-0 px-4">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-balance">
            {invoice.petName} — Subscription invoice {invoice.number ?? 'Invoice'}
          </span>
          <Badge variant={statusColor} size="sm">
            {statusLabel}
          </Badge>
        </div>

        <div className="text-muted-foreground">
          <span className="font-medium">{amount}</span>
        </div>

        <div className="flex items-center gap-1 text-sm text-muted-foreground/60">
          <ExternalLink className="size-3" />
          <span>View invoice</span>
        </div>
      </CardContent>
    </Card>
  )

  return (
    <a
      href={invoice.dashboardInvoiceUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="block"
    >
      {content}
    </a>
  )
}

/** Dispatches a timeline item to the correct card component. */
function TimelineEntryRenderer({ item, isFuture }: { item: TimelineItem; isFuture: boolean }) {
  switch (item.type) {
    case 'appointment':
      return <AppointmentItem appointment={item.data} isFuture={isFuture} />
    case 'conversation':
      return <ConversationItem conversation={item.data} />
    case 'invoice':
      return <InvoiceItem invoice={item.data} />
    case 'communication':
      return <CommunicationEntry communication={item.data} showPatient />
  }
}

/** Badge showing the conversation state (open, closed, snoozed). */
function ConversationStateBadge({ state }: { state: string }) {
  const variants = {
    open: 'success',
    closed: 'neutral',
    snoozed: 'pending',
  } as const

  return (
    <Badge variant={variants[state as keyof typeof variants] ?? 'neutral'} size="sm">
      {state}
    </Badge>
  )
}

/** Modal for setting or clearing a member's Stripe customer ID. */
function EditStripeCustomerIdDialog({
  memberId,
  currentValue,
  open,
  onOpenChange,
}: {
  memberId: string
  currentValue: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [inputValue, setInputValue] = useState(currentValue ?? '')
  const updateMember = useUpdateMember(memberId)

  const stripeIdError = getStripeCustomerIdError(validateStripeId(inputValue, 'customer'))

  function handleOpenChange(next: boolean) {
    if (next) setInputValue(currentValue ?? '')
    onOpenChange(next)
  }

  function handleSave() {
    if (stripeIdError) return
    updateMember.mutate(
      { stripeCustomerId: inputValue.trim() || null },
      { onSuccess: () => onOpenChange(false) }
    )
  }

  function handleRemove() {
    updateMember.mutate({ stripeCustomerId: null }, { onSuccess: () => onOpenChange(false) })
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Stripe Customer ID</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="cus_..."
          />
          {stripeIdError && (
            <Alert variant="destructive">
              <AlertDescription>{stripeIdError}</AlertDescription>
            </Alert>
          )}
          {!stripeIdError && updateMember.error && (
            <p className="text-destructive-fg">{updateMember.error.message}</p>
          )}
        </div>
        <DialogFooter>
          {currentValue && (
            <Button
              variant="ghost-destructive"
              onClick={handleRemove}
              disabled={updateMember.isPending}
              className="mr-auto"
            >
              Remove
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updateMember.isPending || stripeIdError !== null}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function InternalNotesCard({ value, onEdit }: { value: string | null; onEdit: () => void }) {
  return (
    <Card>
      <CardHeader
        className="cursor-pointer"
        role="button"
        tabIndex={0}
        aria-label="Edit internal notes"
        onClick={onEdit}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onEdit()
          }
        }}
      >
        <CardTitle>Internal Notes</CardTitle>
        <CardAction>
          <ChevronRight className="size-4 text-muted-foreground" />
        </CardAction>
      </CardHeader>
      <CardContent>
        {isEmptyHtml(value) ? (
          <p className="text-muted-foreground">No internal notes</p>
        ) : (
          <ExpandableContent>
            <RichTextContent
              html={normaliseForDisplay(value ?? '')}
              className="select-text break-words"
            />
          </ExpandableContent>
        )}
      </CardContent>
    </Card>
  )
}

/** Right-hand sidebar card showing account status. */
function AccountSummaryCard({
  member,
  appointmentCount,
  conversationCount,
  stripeDashboardUrl,
  intercomContact,
  intercomLoaded,
}: {
  member: Member
  appointmentCount: number
  conversationCount: number
  stripeDashboardUrl: string | null
  intercomContact?: { id: string; intercomUrl: string } | null
  intercomLoaded: boolean
}) {
  const [editStripeOpen, setEditStripeOpen] = useState(false)

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Account Summary</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-1 text-sm text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Clock className="size-3 shrink-0" />
            <span>
              Joined <FormattedDate value={member.createdAt} clinicId={null} />{' '}
              <span className="text-muted-foreground/60">
                ({formatRelativeTime(member.createdAt)})
              </span>
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Calendar className="size-3 shrink-0" />
            <span>
              {appointmentCount} {appointmentCount === 1 ? 'appointment' : 'appointments'}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <CreditCard
              className={cn(
                'size-3 shrink-0',
                stripeDashboardUrl ? 'text-success-accent' : 'text-destructive-fg'
              )}
            />
            <span
              className={cn(
                'font-semibold',
                stripeDashboardUrl ? 'text-success-accent' : 'text-destructive-accent'
              )}
            >
              {stripeDashboardUrl ? 'Stripe linked' : 'Stripe not linked'}
            </span>
            <div className="ml-auto flex items-center gap-0.5 -my-1">
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={() => setEditStripeOpen(true)}
                aria-label="Edit Stripe customer ID"
              >
                <Pencil className="size-3.5" />
              </Button>
              {stripeDashboardUrl && (
                <Button
                  variant="ghost"
                  size="icon-xs"
                  render={<a href={stripeDashboardUrl} target="_blank" rel="noopener noreferrer" />}
                  aria-label="View in Stripe"
                >
                  <ExternalLink className="size-3.5" />
                </Button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <MessageSquare className="size-3 shrink-0" />
            <span>
              {intercomContact
                ? `${conversationCount} Intercom ${conversationCount === 1 ? 'conversation' : 'conversations'}`
                : intercomLoaded
                  ? 'No Intercom data'
                  : 'Loading Intercom…'}
            </span>
            {intercomContact && (
              <div className="ml-auto -my-1">
                <Button
                  variant="ghost"
                  size="icon-xs"
                  render={
                    <a
                      href={intercomContact.intercomUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  }
                  aria-label="View in Intercom"
                >
                  <ExternalLink className="size-3.5" />
                </Button>
              </div>
            )}
          </div>
        </CardContent>
        {!member.archivedAt && (
          <CardFooter className="justify-end">
            <ImpersonateButton memberId={member.id} />
          </CardFooter>
        )}
      </Card>
      <EditStripeCustomerIdDialog
        memberId={member.id}
        currentValue={member.stripeCustomerId}
        open={editStripeOpen}
        onOpenChange={setEditStripeOpen}
      />
    </>
  )
}

/**
 * Opens a new tab logged in as the given member by hitting the staff-authed
 * impersonation endpoint. The API sets the member session cookie on the
 * shared host, so the newly-opened customer portal picks it up on load.
 */
function ImpersonateButton({ memberId }: { memberId: string }) {
  const { data: authenticatedStaff } = useGetAuthenticatedStaff()
  const impersonate = useImpersonateMember()

  if (!authenticatedStaff?.isSuperuser) return null

  return (
    <Button
      variant="outline"
      onClick={() => impersonate.mutate(memberId)}
      disabled={impersonate.isPending}
    >
      <VenetianMask className="size-3" />
      Impersonate
    </Button>
  )
}

/** Right-hand sidebar card showing contact details, with a dialog for editing. */
function ContactInformationCard({ member }: { member: Member }) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Contact Information</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-1 text-sm text-muted-foreground">
          <div className="flex items-center gap-3">
            <CircleUser className="size-3.5 shrink-0" />
            <span className="font-medium">
              {member.firstName} {member.lastName}
            </span>
            <RegionFlag region={member.region} className="text-lg" />
            <CopyButton
              value={`${member.firstName} ${member.lastName}`}
              label="Name"
              iconClassName="size-3"
            />
          </div>
          <div className="flex items-center gap-3">
            <Mail className="size-3.5 shrink-0" />
            <span className="min-w-0 break-all">{member.email}</span>
            <CopyButton value={member.email} label="Email" iconClassName="size-3" />
          </div>
          <div className="flex items-center gap-3">
            <Phone className="size-3.5 shrink-0" />
            <span className="tabular-nums">{member.phone}</span>
            <CopyButton value={member.phone} label="Phone" iconClassName="size-3" />
          </div>
          {member.address1 && (
            <div className="flex items-center gap-3">
              <MapPin className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="flex-1 truncate">{formatAddress(member)}</span>
              <CopyButton iconClassName="size-3" value={formatAddress(member)} label="Address" />
            </div>
          )}
        </CardContent>
        <CardFooter className="justify-end">
          <Button variant="outline" onClick={() => setOpen(true)}>
            Edit
          </Button>
        </CardFooter>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Contact Information</DialogTitle>
          </DialogHeader>
          <UpdateMemberForm
            member={member}
            onSuccess={() => setOpen(false)}
            onCancel={() => setOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}

/** Maps a Stripe subscription status to a border color token. */
function subscriptionBorderColor(status: string | null | undefined): string {
  if (!status) return 'border-destructive-fg'
  if (status === 'active') return 'border-success-fg'
  if (status === 'past_due') return 'border-warning-fg'
  return 'border-destructive-fg'
}

/** A single linked pet card within the sidebar. */
function LinkedPetCard({ pet, memberRegion }: { pet: Patient; memberRegion: RegionId }) {
  const attributes = usePatientAttributes(pet, { shortAge: true })
  const { data, isLoading: isSubLoading } = useGetPatientSubscription(pet.id)
  const subscription = data?.status ? data : null
  const borderColor =
    pet.archivedAt || isSubLoading
      ? 'border-foreground/20'
      : subscriptionBorderColor(subscription?.status)

  return (
    <Link
      to={generatePath(routes.patient, { patientId: pet.id })}
      className={cn('flex items-center gap-3 border-l-[3px] py-1 pl-3 pr-2', borderColor)}
    >
      <div className="flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <p
            className={cn(
              'font-semibold text-balance',
              pet.archivedAt && 'text-muted-foreground line-through'
            )}
          >
            {pet.name}
          </p>
          {pet.archivedAt !== null ? (
            <Badge variant="secondary" size="sm">
              <Archive className="mr-1" />
              Archived
            </Badge>
          ) : isSubLoading ? (
            <span className="h-5 w-16 animate-pulse rounded-full bg-muted" />
          ) : subscription ? (
            <SubscriptionBadges
              status={subscription.status}
              interval={subscription.interval}
              intervalCount={subscription.intervalCount}
              hasFleaWormTickPlan={subscription.hasFleaWormTickPlan}
              memberRegion={memberRegion}
            />
          ) : (
            <Badge variant="destructive" size="sm">
              No Subscription
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          {(['species', 'breed', 'sex', 'dob'] as const).map((key) => {
            const { icon, label, value } = attributes[key]
            return (
              <span
                key={key}
                className="flex min-w-0 items-center gap-0.5 [&>svg]:size-3 [&>svg]:shrink-0"
              >
                {icon}
                <span className="sr-only">{label}: </span>
                <span className="min-w-0 break-words">{value}</span>
              </span>
            )
          })}
        </div>
      </div>
      <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
    </Link>
  )
}

function LinkedPatientsCard({
  memberId,
  pets,
  memberArchived,
  memberRegion,
}: {
  memberId: string
  pets: Patient[]
  memberArchived: boolean
  memberRegion: RegionId
}) {
  const [open, setOpen] = useState(false)

  const sortedPets = [
    ...pets.filter((pet) => pet.archivedAt === null),
    ...pets.filter((pet) => pet.archivedAt !== null),
  ]

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Pets</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {sortedPets.length === 0 ? (
            <p className="text-muted-foreground">No patients yet</p>
          ) : (
            sortedPets.map((pet) => (
              <LinkedPetCard key={pet.id} pet={pet} memberRegion={memberRegion} />
            ))
          )}
        </CardContent>
        {!memberArchived && (
          <CardFooter className="justify-end">
            <Button onClick={() => setOpen(true)}>
              <PawPrint />
              Add pet
            </Button>
          </CardFooter>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>Add Patient</DialogTitle>
          </DialogHeader>
          <CreatePatientForm
            memberId={memberId}
            onSuccess={() => setOpen(false)}
            onCancel={() => setOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}

/* ------------------------------------------------------------------ */
/*  Main page                                                         */
/* ------------------------------------------------------------------ */

/** Single member detail view. */
export function MemberPage() {
  // Route config guarantees memberId is always present
  const { memberId } = useParams<{ memberId: string }>() as { memberId: string }
  const { data: memberData, isLoading, error } = useGetMember(memberId)
  const { data: appointments, isLoading: isAppointmentsLoading } = useListMemberAppointments(
    memberId,
    !!memberData
  )
  const { data: intercom, isLoading: isIntercomLoading } = useListMemberConversations(
    memberId,
    !!memberData
  )
  const { data: communications, isLoading: isCommunicationsLoading } =
    useListMemberCommunications(memberId)
  const updateMemberNotes = useUpdateMember(memberId)
  const { data: authenticatedStaff } = useGetAuthenticatedStaff()
  const [archiveOpen, setArchiveOpen] = useState(false)
  const [internalNotesOpen, setInternalNotesOpen] = useState(false)

  function openInternalNotes() {
    updateMemberNotes.reset()
    setInternalNotesOpen(true)
  }
  // Headers resolve days in the viewer's own frame, matching the entries beneath.
  const viewerFmt = useViewerFormat()

  useBreadcrumbs(
    memberData
      ? [
          { label: 'Members', to: routes.members },
          { label: `${memberData.member.firstName} ${memberData.member.lastName}` },
        ]
      : undefined
  )

  useDocumentTitle(
    memberData ? `${memberData.member.firstName} ${memberData.member.lastName}` : undefined
  )

  const { invoices: petInvoices, isLoading: isInvoicesLoading } = useListPatientInvoices(
    memberData?.member.pets?.map((p) => p.id) ?? []
  )

  const isTimelineLoading =
    isAppointmentsLoading || isIntercomLoading || isInvoicesLoading || isCommunicationsLoading

  if (error) return <div>Error: {error.message}</div>
  if (isLoading || !memberData) return <div>Loading...</div>

  const { member, stripeDashboardUrl } = memberData

  const timelineItems: TimelineItem[] = [
    ...(appointments ?? []).map((a) => ({
      type: 'appointment' as const,
      date: a.startTime,
      data: a,
    })),
    ...(intercom?.conversations ?? []).map((c) => ({
      type: 'conversation' as const,
      date: c.createdAt,
      data: c,
    })),
    // Dated by `sentAt` — see `getPatientTimeline`, which does the same on the patient side.
    ...(communications ?? []).map((c) => ({
      type: 'communication' as const,
      date: c.sentAt,
      data: c,
    })),
    ...petInvoices.flatMap(({ patientId, invoices }) => {
      const petName = member.pets?.find((p) => p.id === patientId)?.name ?? ''
      return invoices.map((inv) => ({
        type: 'invoice' as const,
        date: inv.paidAt ?? inv.createdAt,
        data: { ...inv, petName },
      }))
    }),
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

  const groups = groupByDate(timelineItems, viewerFmt)
  const dividerIndex = nowDividerIndex(groups)

  return (
    <div className="space-y-6">
      {member.archivedAt && (
        <Alert variant="warning">
          <Archive />
          <AlertTitle>
            Archived on <FormattedDate value={member.archivedAt} clinicId={null} />
          </AlertTitle>
          {member.archivedReason && <AlertDescription>{member.archivedReason}</AlertDescription>}
        </Alert>
      )}

      <div className="flex flex-col-reverse gap-3 lg:flex-row lg:items-center lg:justify-between">
        <h1 className="text-headline-lg text-balance">
          {member.firstName} {member.lastName}
        </h1>
        <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
          {authenticatedStaff?.isSuperuser && (
            <Button
              size="lg"
              variant={member.archivedAt ? 'outline' : 'destructive'}
              onClick={() => setArchiveOpen(true)}
            >
              {member.archivedAt ? (
                <ArchiveRestore className="mr-1.5 size-4" />
              ) : (
                <Archive className="mr-1.5 size-4" />
              )}
              {member.archivedAt ? 'Unarchive' : 'Archive'}
            </Button>
          )}
        </div>
      </div>

      <ArchiveMemberDialog
        memberId={memberId}
        archivedAt={member.archivedAt}
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
      />

      {internalNotesOpen && (
        <InternalNotesDialog
          key={member.id}
          value={member.internalNotes}
          onClose={() => setInternalNotesOpen(false)}
          onSave={(internalNotes) => {
            updateMemberNotes.mutate(
              { internalNotes },
              { onSuccess: () => setInternalNotesOpen(false) }
            )
          }}
          isSaving={updateMemberNotes.isPending}
          error={updateMemberNotes.error}
        />
      )}

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px]">
        {/* Activity timeline */}
        {/* `min-w-0` keeps the timeline within the grid track — without it,
            an unbreakable token in a conversation body (URL, long word) can
            expand the `1fr` column and push the whole page wider. */}
        <section className="min-w-0 space-y-4">
          <h2 className="text-headline-md">Activity</h2>

          {isTimelineLoading && timelineItems.length === 0 ? (
            <div className="flex justify-center py-4">
              <Spinner className="size-4 text-muted-foreground" />
            </div>
          ) : groups.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">No activity yet</p>
          ) : (
            <div className="space-y-6">
              {groups.map((group, i) => (
                <div key={group.label}>
                  {i === dividerIndex && <TimelineNowDivider />}

                  {/* Date group header — its rule goes dashed too, so the whole day
                      above the boundary reads as provisional. */}
                  <div className="sticky top-0 z-10 mb-3 flex items-center gap-3">
                    <span className="shrink-0 text-sm font-medium text-muted-foreground">
                      {group.label}
                    </span>
                    <div
                      className={cn(
                        'flex-1',
                        group.isFuture ? 'border-t border-dashed border-border' : 'h-px bg-border'
                      )}
                    />
                  </div>

                  {/* Entries with left connector line */}
                  <div className="relative space-y-4 pl-9">
                    {/* Vertical connector — dashed while the entries above the
                        boundary haven't happened yet. */}
                    <div
                      className={cn(
                        'absolute top-0 bottom-0 left-3 -translate-x-1/2',
                        group.isFuture ? 'border-l border-dashed border-border' : 'w-px bg-border'
                      )}
                    />

                    {group.items.map((item) => (
                      <div key={`${item.type}-${item.data.id}`} className="relative">
                        {/* Icon on the connector line */}
                        <div className="absolute -left-9 top-3">
                          <TimelineIcon type={item.type} isFuture={group.isFuture} />
                        </div>
                        <TimelineEntryRenderer item={item} isFuture={group.isFuture} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {isTimelineLoading && (
                <div className="flex justify-center py-4">
                  <Spinner className="size-4 text-muted-foreground" />
                </div>
              )}
            </div>
          )}
        </section>

        {/* Sidebar */}
        <aside className="space-y-4">
          <AccountSummaryCard
            member={member}
            appointmentCount={appointments?.length ?? 0}
            conversationCount={intercom?.conversations?.length ?? 0}
            stripeDashboardUrl={stripeDashboardUrl}
            intercomContact={intercom?.contact}
            intercomLoaded={!isIntercomLoading}
          />
          <InternalNotesCard value={member.internalNotes} onEdit={openInternalNotes} />
          <ContactInformationCard member={member} />
          <LinkedPatientsCard
            memberId={memberId}
            pets={member.pets ?? []}
            memberArchived={member.archivedAt !== null}
            memberRegion={member.region}
          />
          <MemberInvoicesCard memberId={memberId} />
        </aside>
      </div>
    </div>
  )
}
